import {EditorSelection} from '@codemirror/state'
import type {Command, EditorView} from '@codemirror/view'

import {getNodeAtOffset} from '../../language-tools/syntax-tree/node-range-utils'
import {getMarkdownTree} from '../extensions/markdown-parser'
import {createText} from '../extensions/single-line-editor'
import {expandRangeToLineEnds, hasNonEmptySelection} from '../utils'

/**
 * Check if the caret location is within a code block.
 */
function isCodeBlock(view: EditorView) {
  const caretLocation = view.state.selection.main.anchor
  const tree = getMarkdownTree(view.state)
  const relevantNode = getNodeAtOffset(tree, caretLocation)

  return relevantNode?.type === 'code'
}

/**
 * Dispatch an undoable transaction to indent a line within the code block.
 */
export const indentLineIfCodeBlock: Command = (view: EditorView) => {
  const caretLocation = view.state.selection.main.anchor
  const {from: lineFrom} = expandRangeToLineEnds(view.state)

  if (isCodeBlock(view)) {
    if (hasNonEmptySelection(view.state)) {
      // Adding a tab when text is selected.
      view.dispatch(
        view.state.changeByRange(range => ({
          changes: [
            {
              from: lineFrom,
              insert: createText('\t'),
            },
          ],
          range: EditorSelection.range(range.from + 1, range.to + 1),
          userEvent: 'input.type',
          scrollIntoView: true,
        })),
      )
    } else {
      // Adding a tab when no text is selected.
      view.dispatch({
        changes: [
          {
            from: caretLocation,
            insert: createText('\t'),
          },
        ],
        selection: EditorSelection.single(caretLocation + 1),
        userEvent: 'input.type',
        scrollIntoView: true,
      })
    }

    return true // handled
  }

  return false
}

export const unindentLineIfCodeBlock: Command = (view: EditorView) => {
  const caretLocation = view.state.selection.main.anchor
  const document = view.state.doc.toString()

  const {from: lineFrom, to: lineTo} = expandRangeToLineEnds(view.state)

  if (isCodeBlock(view)) {
    const lineText = document.slice(lineFrom, lineTo)
    if (lineText.startsWith('\t')) {
      view.dispatch({
        changes: [
          {
            from: lineFrom,
            to: lineFrom + 1,
            insert: createText(''),
          },
        ],
        selection: EditorSelection.single(caretLocation - 1),
        userEvent: 'input.type',
        scrollIntoView: true,
      })
      return true // handled
    } else if (lineText.startsWith('    ')) {
      view.dispatch({
        changes: [
          {
            from: caretLocation,
            to: caretLocation + 4,
            insert: createText(''),
          },
        ],
        selection: EditorSelection.single(caretLocation - 4),
        userEvent: 'input.type',
        scrollIntoView: true,
      })
      return true // handled
    } else {
      return true // handled
    }
  }

  return false
}
